import { type AudienceCriteria, parseContactCsv } from "@wcp/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { avatarStyle, initialsFor } from "../../lib/avatar.js";
import { useBusiness } from "../../lib/business.js";
import { useMe } from "../auth/api.js";
import {
  type AudienceView,
  type ContactFilters,
  type ContactRow,
  type ContactSort,
  type ImportResult,
  type Segment,
  useAudiences,
  useContacts,
  useCreateAudience,
  useFacets,
  useImportContacts,
  useRenameContact,
  useSetSegment,
  useThresholds,
  useUpdateThresholds,
  useUploadAudience,
} from "./api.js";
import styles from "./ContactsScreen.module.css";
import { Ic } from "./icons.js";

const SEG_LABEL: Record<Segment, string> = { new: "New", repeat: "Repeat buyer", vip: "VIP" };
const SEG_ORDER: Segment[] = ["new", "repeat", "vip"];
const INLINE_CAP = 6;

/** Map a contact's source_platform back to an AudienceCriteria.platform enum value. */
function platformEnum(source: string): "shopify" | "woocommerce" | null {
  const s = source.toLowerCase();
  if (s.includes("shopify")) return "shopify";
  if (s.includes("woo")) return "woocommerce";
  return null;
}

function fmtRM(n: number): string {
  return `RM${Math.round(n).toLocaleString()}`;
}

function fmtLast(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  if (sameDay) return `Today · ${hh}:${mm}`;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${d.getDate()} ${months[d.getMonth()]}`;
}

type ColKey = "name" | "phone" | "segment" | "orders" | "source" | "audience" | "last" | "optin";
type FilterKey = "segment" | "source" | "optin";

interface ColDef {
  key: ColKey;
  label: string;
  anchor?: boolean;
  num?: boolean;
  sort?: ContactSort;
  filterKey?: FilterKey;
}

const COLS: ColDef[] = [
  { key: "name", label: "Name", anchor: true, sort: "name" },
  { key: "phone", label: "Number", sort: "phone" },
  { key: "segment", label: "Segment", sort: "segment", filterKey: "segment" },
  { key: "orders", label: "Orders · spend", num: true, sort: "spend" },
  { key: "source", label: "Source", sort: "source", filterKey: "source" },
  { key: "audience", label: "Audience" },
  { key: "last", label: "Last contact" },
  { key: "optin", label: "Opt-in", sort: "optin", filterKey: "optin" },
];

export function ContactsScreen() {
  const { activeId } = useBusiness();
  // key={activeId} remounts → per-business reset of all local table state.
  return <ContactsBody key={activeId} businessId={activeId} />;
}

function ContactsBody({ businessId }: { businessId: string }) {
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [colFilters, setColFilters] = useState<Record<FilterKey, Set<string>>>({
    segment: new Set(),
    source: new Set(),
    optin: new Set(),
  });
  const [sort, setSort] = useState<{ key: ContactSort; dir: "asc" | "desc" }>({
    key: "name",
    dir: "asc",
  });
  const [visibleCols, setVisibleCols] = useState<Set<ColKey>>(
    () => new Set(COLS.map((c) => c.key)),
  );
  const [activeAudience, setActiveAudience] = useState<AudienceView | null>(null);
  const [colMenu, setColMenu] = useState(false);
  const [audMenu, setAudMenu] = useState(false);
  const [audQuery, setAudQuery] = useState("");
  const [hf, setHf] = useState<{ key: FilterKey; x: number; y: number } | null>(null);
  const [modal, setModal] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [toast, setToast] = useState<{ label: string; count: number } | null>(null);
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [editRule, setEditRule] = useState(false);

  const facets = useFacets(businessId);
  const thresholds = useThresholds(businessId);
  const vipSpend = thresholds.data?.vipSpend ?? 4000;
  const vipOrders = thresholds.data?.vipOrders ?? 8;

  // Server-side filters derived from the active audience OR the table funnels/search.
  const filters: ContactFilters = useMemo(() => {
    if (activeAudience) {
      const d = activeAudience.definition;
      return {
        ...(d.segment?.length ? { segment: d.segment } : {}),
        ...(d.platform?.length ? { platform: d.platform } : {}),
        ...(d.region?.length ? { region: d.region } : {}),
        ...(d.q ? { q: d.q } : {}),
        sort: sort.key,
        dir: sort.dir,
      };
    }
    const optinSel = [...colFilters.optin] as ("Yes" | "No")[];
    return {
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(colFilters.segment.size ? { segment: [...colFilters.segment] } : {}),
      ...(colFilters.source.size ? { platform: [...colFilters.source] } : {}),
      ...(optinSel.length === 1 ? { optin: optinSel } : {}),
      sort: sort.key,
      dir: sort.dir,
    };
  }, [activeAudience, q, colFilters, sort]);

  const contacts = useContacts(businessId, filters);
  const audiences = useAudiences(businessId);
  const setSeg = useSetSegment(businessId);

  const rows = contacts.data?.pages.flatMap((p) => p.contacts) ?? [];
  const filteredTotal = contacts.data?.pages[0]?.total ?? 0;
  const grandTotal = facets.data?.total ?? filteredTotal;
  const optedIn = facets.data?.optedIn ?? 0;
  const remaining = filteredTotal - rows.length;

  const anyFilter =
    !!q.trim() ||
    colFilters.segment.size > 0 ||
    colFilters.source.size > 0 ||
    colFilters.optin.size > 0;

  // ---- audience chips: cap inline, promote the active one, overflow popover ----
  const allAuds = audiences.data ?? [];
  const activeAudObj = activeAudience
    ? (allAuds.find((a) => a.id === activeAudience.id) ?? null)
    : null;
  let inlineAuds = allAuds.slice(0, INLINE_CAP);
  if (activeAudObj && !inlineAuds.some((a) => a.id === activeAudObj.id)) {
    inlineAuds = [activeAudObj, ...inlineAuds.slice(0, INLINE_CAP - 1)];
  }
  const overflowAuds = allAuds.filter((a) => !inlineAuds.some((i) => i.id === a.id));
  const overflowActive = !!activeAudObj && overflowAuds.some((a) => a.id === activeAudObj.id);
  const aq = audQuery.trim().toLowerCase();
  const audMenuList = allAuds.filter((a) => !aq || a.label.toLowerCase().includes(aq));
  const audMenuCustom = audMenuList.filter((a) => a.kind !== "builtin");
  const audMenuBuilt = audMenuList.filter((a) => a.kind === "builtin");

  const sources = facets.data?.sources ?? [];
  const FILTER_VALUES: Record<FilterKey, string[]> = {
    segment: SEG_ORDER,
    source: sources,
    optin: ["Yes", "No"],
  };

  const overrideCount = rows.filter((c) => c.segment_is_manual).length;

  // ---- auto-load sentinel (capture phase) ----
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const moreRef = useRef(false);
  moreRef.current = contacts.hasNextPage === true && !contacts.isFetchingNextPage;
  useEffect(() => {
    const onScroll = () => {
      const el = sentinelRef.current;
      if (!el || !moreRef.current) return;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (el.getBoundingClientRect().top < vh + 240) contacts.fetchNextPage();
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [contacts.fetchNextPage]);

  // ---- Esc closes any open popover ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setColMenu(false);
      setAudMenu(false);
      setHf(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  function toggleCol(key: ColKey) {
    setVisibleCols((s) => {
      const n = new Set(s);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  }
  function toggleSort(key: ContactSort | undefined, num: boolean | undefined) {
    if (!key) return;
    setActiveAudience(null);
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: num ? "desc" : "asc" },
    );
  }
  function toggleCF(kind: FilterKey, val: string) {
    setActiveAudience(null);
    setColFilters((prev) => {
      const set = new Set(prev[kind]);
      set.has(val) ? set.delete(val) : set.add(val);
      return { ...prev, [kind]: set };
    });
  }
  function clearCF(kind: FilterKey) {
    setColFilters((prev) => ({ ...prev, [kind]: new Set() }));
  }
  function openHF(key: FilterKey) {
    return (e: React.MouseEvent<HTMLButtonElement>) => {
      const r = e.currentTarget.getBoundingClientRect();
      const winW = window.innerWidth || 1200;
      const x = Math.max(8, Math.min(r.left - 60, winW - 196));
      setHf((cur) => (cur && cur.key === key ? null : { key, x, y: r.bottom + 6 }));
    };
  }

  const shownCols = COLS.filter((c) => c.anchor || visibleCols.has(c.key));

  // Serialise the active table funnels/search into AudienceCriteria for "Save N".
  const captured: AudienceCriteria = useMemo(() => {
    const platform = [...colFilters.source]
      .map(platformEnum)
      .filter((p): p is "shopify" | "woocommerce" => p !== null);
    return {
      ...(colFilters.segment.size ? { segment: [...colFilters.segment] as Segment[] } : {}),
      ...(platform.length ? { platform } : {}),
      // R-AUD-2: audiences are always opted-in — only persist optin when "Yes" is selected.
      ...(colFilters.optin.has("Yes") ? { optin: [true as const] } : {}),
      ...(q.trim() ? { q: q.trim() } : {}),
    };
  }, [colFilters, q]);

  const drawerContact = drawerId == null ? null : (rows.find((c) => c.id === drawerId) ?? null);

  return (
    <section className={styles.screen}>
      <div className={styles.head}>
        <div>
          <h1 className={styles.cpH}>Contacts</h1>
          <p className={styles.cpSub}>
            {grandTotal} contacts · {optedIn} opted-in · segment auto-assigned from order history
          </p>
        </div>
        <div className={styles.headActions}>
          {isAdmin && (
            <button
              type="button"
              className={`btn ${styles.headBtn}`}
              onClick={() => setImportOpen(true)}
            >
              Import contacts
            </button>
          )}
          <button
            type="button"
            className={`btn btn--primary ${styles.headBtn}`}
            onClick={() => setModal(true)}
          >
            {Ic.plus}{" "}
            {anyFilter ? `Save ${filteredTotal.toLocaleString()} as audience` : "Create audience"}
          </button>
        </div>
      </div>

      {/* audience chip bar — single horizontal scroller + overflow/add/clear pinned outside */}
      <div className={styles.audBar}>
        <span className={styles.audBarH}>Audiences</span>
        <div className={styles.audBarChips}>
          {inlineAuds.map((a) => (
            <button
              key={a.id}
              type="button"
              className={`${styles.audChip}${activeAudObj?.id === a.id ? ` ${styles.on}` : ""}${
                a.kind !== "builtin" ? ` ${styles.custom}` : ""
              }`}
              onClick={() => setActiveAudience((cur) => (cur?.id === a.id ? null : a))}
            >
              {a.label}
              <span className={styles.audChipN}>{a.count.toLocaleString()}</span>
            </button>
          ))}
        </div>
        <div className={styles.audBarEnd}>
          {overflowAuds.length > 0 && (
            <div className={styles.audMoreWrap}>
              <button
                type="button"
                className={`${styles.audChip} ${styles.more}${overflowActive ? ` ${styles.on}` : ""}`}
                onClick={() => {
                  setAudMenu((v) => !v);
                  setAudQuery("");
                }}
                aria-expanded={audMenu}
              >
                +{overflowAuds.length} more {Ic.chevron}
              </button>
              {audMenu && (
                <>
                  <button
                    type="button"
                    className={styles.menuCatch}
                    aria-label="Close audiences menu"
                    onClick={() => setAudMenu(false)}
                  />
                  <div className={`${styles.filterMoreMenu} ${styles.audMenu}`}>
                    <div className={styles.audMenuSearch}>
                      {Ic.search}
                      <input
                        placeholder="Find audience…"
                        value={audQuery}
                        onChange={(e) => setAudQuery(e.target.value)}
                      />
                    </div>
                    <div className={styles.audMenuList}>
                      {audMenuList.length === 0 && (
                        <div className={styles.audMenuEmpty}>No audiences match.</div>
                      )}
                      {audMenuCustom.length > 0 && (
                        <div className={styles.audMenuGrp}>Your audiences</div>
                      )}
                      {audMenuCustom.map((a) => (
                        <AudMenuRow
                          key={a.id}
                          a={a}
                          active={activeAudObj?.id === a.id}
                          onClick={() => {
                            setActiveAudience((cur) => (cur?.id === a.id ? null : a));
                            setAudMenu(false);
                          }}
                        />
                      ))}
                      {audMenuBuilt.length > 0 && <div className={styles.audMenuGrp}>Built-in</div>}
                      {audMenuBuilt.map((a) => (
                        <AudMenuRow
                          key={a.id}
                          a={a}
                          active={activeAudObj?.id === a.id}
                          onClick={() => {
                            setActiveAudience((cur) => (cur?.id === a.id ? null : a));
                            setAudMenu(false);
                          }}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      className={styles.audMenuCreate}
                      onClick={() => {
                        setAudMenu(false);
                        setModal(true);
                      }}
                    >
                      {Ic.plus} Create audience
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          <button
            type="button"
            className={`${styles.audChip} ${styles.add}`}
            onClick={() => setModal(true)}
            title="Create audience"
            aria-label="Create audience"
          >
            {Ic.plus}
          </button>
          {activeAudObj && (
            <button
              type="button"
              className={styles.audBarClear}
              onClick={() => setActiveAudience(null)}
            >
              Clear ✕
            </button>
          )}
        </div>
      </div>

      {/* toolbar — search + columns picker */}
      <div className={`${styles.toolbar} ${styles.campToolbar}`}>
        <div className={styles.searchbox}>
          {Ic.search}
          <input
            placeholder="Search name or number…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActiveAudience(null);
            }}
          />
        </div>
        <div className={`${styles.filterMoreWrap} ${styles.campCols}`}>
          <button
            type="button"
            className={`${styles.chipBtn} ${styles.colsBtn}`}
            onClick={() => setColMenu((v) => !v)}
            aria-expanded={colMenu}
          >
            {Ic.cols}
            Columns
          </button>
          {colMenu && (
            <>
              <button
                type="button"
                className={styles.menuCatch}
                aria-label="Close columns menu"
                onClick={() => setColMenu(false)}
              />
              <div className={`${styles.filterMoreMenu} ${styles.colMenu}`}>
                <div className={styles.colMenuH}>Show columns</div>
                {COLS.map((c) => {
                  const on = !!c.anchor || visibleCols.has(c.key);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      className={`${styles.moreOpt} ${styles.colOpt}${on ? ` ${styles.on}` : ""}`}
                      disabled={c.anchor}
                      onClick={() => !c.anchor && toggleCol(c.key)}
                    >
                      <span className={styles.colCheck}>{on ? Ic.check : null}</span>
                      {c.label}
                      {c.anchor ? <span className={styles.colLock}>fixed</span> : null}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* auto-segment legend + threshold editor */}
      <div className={styles.segRule}>
        <span className={styles.segRuleH}>Auto-assigned</span>
        <span>
          <b>New</b> — no orders yet
        </span>
        <span>
          <b>Repeat buyer</b> — 1–{vipOrders - 1} orders
        </span>
        <span>
          <b>VIP</b> — {fmtRM(vipSpend)}+ spent or {vipOrders}+ orders
        </span>
        {overrideCount > 0 && (
          <span className={styles.segRuleOvr}>
            {overrideCount} manual override{overrideCount > 1 ? "s" : ""}
          </span>
        )}
        {isAdmin && (
          <button
            type="button"
            className={styles.segRuleEditBtn}
            onClick={() => setEditRule((v) => !v)}
          >
            {editRule ? "Done" : "Edit rule"}
          </button>
        )}
      </div>

      {isAdmin && editRule && <ThresholdEditor businessId={businessId} />}

      <div className={styles.listCount}>
        Showing <b>{rows.length}</b> of <b>{filteredTotal}</b>
        {filteredTotal !== grandTotal ? ` (filtered from ${grandTotal})` : ""}
      </div>

      <div className={styles.panel}>
        <div className={styles.contactsScroll}>
          <table className={`${styles.dataTable} ${styles.contactsTable}`}>
            <thead>
              <tr>
                {shownCols.map((c) => {
                  const sorted = sort.key === c.sort;
                  const cfSet = c.filterKey ? colFilters[c.filterKey] : null;
                  return (
                    <th
                      key={c.key}
                      className={`${styles.thCell}${c.num ? ` ${styles.num}` : ""}${
                        sorted ? ` ${styles.sorted}` : ""
                      }`}
                    >
                      <span className={styles.thIn}>
                        {c.sort ? (
                          <button
                            type="button"
                            className={styles.thSort}
                            onClick={() => toggleSort(c.sort, c.num)}
                            title="Sort"
                          >
                            <span className={styles.thLbl}>{c.label}</span>
                            <span className={styles.thArrow}>
                              {sorted ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                            </span>
                          </button>
                        ) : (
                          <span className={`${styles.thSort} ${styles.thStatic}`}>
                            <span className={styles.thLbl}>{c.label}</span>
                          </span>
                        )}
                        {c.filterKey && (
                          <button
                            type="button"
                            className={`${styles.thFilter}${cfSet?.size ? ` ${styles.on}` : ""}`}
                            onClick={openHF(c.filterKey)}
                            aria-label={`Filter ${c.label}`}
                            aria-expanded={hf?.key === c.filterKey}
                          >
                            {Ic.funnel}
                          </button>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  {shownCols.map((col) => (
                    <Cell
                      key={col.key}
                      col={col}
                      c={c}
                      onOpenDrawer={() => setDrawerId(c.id)}
                      onSetSegment={(override) => setSeg.mutate({ id: c.id, override })}
                    />
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={shownCols.length} className={styles.empty}>
                    No contacts match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {remaining > 0 && (
        <div className={styles.listMore} ref={sentinelRef}>
          <button
            type="button"
            className={styles.loadMoreBtn}
            disabled={contacts.isFetchingNextPage}
            onClick={() => contacts.fetchNextPage()}
          >
            {contacts.isFetchingNextPage ? "Loading…" : `Load ${Math.min(25, remaining)} more`}{" "}
            <span className={styles.lmRem}>· {remaining} left</span>
          </button>
        </div>
      )}

      {/* header-filter dropdown (fixed → never clips; full-width sheet ≤560px) */}
      {hf && (
        <>
          <button
            type="button"
            className={styles.menuCatch}
            aria-label="Close filter"
            onClick={() => setHf(null)}
          />
          <div
            className={`${styles.filterMoreMenu} ${styles.hfMenu}`}
            style={{ position: "fixed", left: hf.x, top: hf.y }}
          >
            <div className={styles.colMenuH}>Filter {hf.key === "optin" ? "opt-in" : hf.key}</div>
            {FILTER_VALUES[hf.key].map((v) => {
              const label = hf.key === "segment" ? SEG_LABEL[v as Segment] : v;
              const on = colFilters[hf.key].has(v);
              return (
                <button
                  key={v}
                  type="button"
                  className={`${styles.moreOpt} ${styles.segOpt}${on ? ` ${styles.on}` : ""}`}
                  onClick={() => toggleCF(hf.key, v)}
                >
                  <span className={styles.colCheck}>{on ? Ic.check : null}</span>
                  {label}
                </button>
              );
            })}
            {colFilters[hf.key].size > 0 && (
              <button
                type="button"
                className={styles.hfClear}
                onClick={() => {
                  clearCF(hf.key);
                  setHf(null);
                }}
              >
                Clear filter
              </button>
            )}
          </div>
        </>
      )}

      {modal && (
        <CreateAudienceModal
          businessId={businessId}
          captured={captured}
          anyFilter={anyFilter}
          matchCount={filteredTotal}
          totalCount={grandTotal}
          existing={allAuds.map((a) => a.label)}
          onClose={() => setModal(false)}
          onCreated={(label, count) => {
            setModal(false);
            setToast({ label, count });
            window.setTimeout(() => setToast((cur) => (cur?.label === label ? null : cur)), 4200);
          }}
        />
      )}
      {importOpen && (
        <ImportContactsModal businessId={businessId} onClose={() => setImportOpen(false)} />
      )}

      {toast && (
        <div className={styles.audToast} role="status">
          <span className={styles.audToastIc}>{Ic.check}</span>
          <span className={styles.audToastT}>
            Audience <b>{toast.label}</b> created · {toast.count.toLocaleString()} contacts
          </span>
          <button type="button" className={styles.audToastX} onClick={() => setToast(null)}>
            ✕
          </button>
        </div>
      )}

      {drawerContact && (
        <ContactDrawer
          c={drawerContact}
          businessId={businessId}
          onClose={() => setDrawerId(null)}
          onOpenChat={() => {
            setDrawerId(null);
            navigate("/inbox");
          }}
        />
      )}
    </section>
  );
}

function AudMenuRow({
  a,
  active,
  onClick,
}: {
  a: AudienceView;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.audMenuRow}${active ? ` ${styles.on}` : ""}`}
      onClick={onClick}
    >
      {a.kind !== "builtin" && <span className={styles.audMenuDot} />}
      <span className={styles.audMenuLbl}>{a.label}</span>
      <span className={styles.audMenuN}>{a.count.toLocaleString()}</span>
      {active && <span className={styles.audMenuCheck}>{Ic.check}</span>}
    </button>
  );
}

/** Renders one table cell for the given column + contact. */
function Cell({
  col,
  c,
  onOpenDrawer,
  onSetSegment,
}: {
  col: ColDef;
  c: ContactRow;
  onOpenDrawer: () => void;
  onSetSegment: (override: Segment | null) => void;
}) {
  switch (col.key) {
    case "name":
      return (
        <td className={styles.strong} data-label="Name">
          <button type="button" className={styles.cnameBtn} onClick={onOpenDrawer}>
            {c.name ?? (c.wa_name ? `~ ${c.wa_name}` : "—")}
          </button>
        </td>
      );
    case "phone":
      return (
        <td className={styles.mono} data-label="Number">
          {c.phone}
        </td>
      );
    case "segment":
      return (
        <td data-label="Segment">
          <SegmentCell c={c} onSetSegment={onSetSegment} />
        </td>
      );
    case "orders":
      return (
        <td className={`${styles.mono} ${styles.dim}`} data-label="Orders">
          {c.orders_count === 0
            ? "— no orders"
            : `${c.orders_count} · ${fmtRM(Number(c.total_spend))}`}
        </td>
      );
    case "source":
      return (
        <td className={styles.dim} data-label="Source">
          {c.source_platform ?? "—"}
        </td>
      );
    case "audience":
      return (
        <td data-label="Audience">
          <span className={styles.audNone}>—</span>
        </td>
      );
    case "last":
      return (
        <td className={`${styles.mono} ${styles.dim}`} data-label="Last">
          {fmtLast(c.last_contact_at)}
        </td>
      );
    case "optin":
      return (
        <td data-label="Opt-in">
          {c.optin ? (
            <span className={`${styles.optin} ${styles.optinYes}`}>{Ic.check} Yes</span>
          ) : (
            <span className={`${styles.optin} ${styles.optinNo}`}>No</span>
          )}
        </td>
      );
    default:
      return <td />;
  }
}

function SegmentCell({
  c,
  onSetSegment,
}: {
  c: ContactRow;
  onSetSegment: (override: Segment | null) => void;
}) {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const manual = c.segment_is_manual;
  const seg = c.segment;

  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenu(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menu]);

  return (
    <div className={styles.segCell}>
      <button
        type="button"
        className={styles.segBtn}
        aria-haspopup="menu"
        aria-expanded={!!menu}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const winW = window.innerWidth || document.documentElement.clientWidth;
          const x = Math.max(8, Math.min(r.left, winW - 176 - 8));
          setMenu((cur) => (cur ? null : { x, y: r.bottom + 5 }));
        }}
        title={manual ? "Manually set — click to change" : "Auto-assigned — click to override"}
      >
        <span className={`${styles.tagpill} ${seg === "vip" ? styles.info : styles.muted}`}>
          <span className={`${styles.segAuto}${manual ? ` ${styles.manual}` : ""}`}>
            {SEG_LABEL[seg]}
          </span>
        </span>
        {manual && <span className={styles.segTagManual}>manual</span>}
        <span className={styles.segCaret}>{Ic.chevron}</span>
      </button>
      {menu && (
        <>
          <button
            type="button"
            className={styles.menuCatch}
            aria-label="Close segment menu"
            onClick={() => setMenu(null)}
          />
          <div
            className={`${styles.filterMoreMenu} ${styles.segMenu}`}
            style={{ position: "fixed", left: menu.x, top: menu.y }}
          >
            <button
              type="button"
              className={`${styles.moreOpt} ${styles.segOpt}${!manual ? ` ${styles.on}` : ""}`}
              onClick={() => {
                onSetSegment(null);
                setMenu(null);
              }}
            >
              <span className={styles.colCheck}>{!manual ? Ic.check : null}</span>Auto
              <span className={styles.segOptHint}>{SEG_LABEL[c.segment_auto]}</span>
            </button>
            <div className={styles.segMenuDiv} />
            {SEG_ORDER.map((o) => (
              <button
                key={o}
                type="button"
                className={`${styles.moreOpt} ${styles.segOpt}${
                  manual && seg === o ? ` ${styles.on}` : ""
                }`}
                onClick={() => {
                  onSetSegment(o);
                  setMenu(null);
                }}
              >
                <span className={styles.colCheck}>{manual && seg === o ? Ic.check : null}</span>
                {SEG_LABEL[o]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ThresholdEditor({ businessId }: { businessId: string }) {
  const t = useThresholds(businessId);
  const update = useUpdateThresholds(businessId);
  const [spend, setSpend] = useState<number | null>(null);
  const [orders, setOrders] = useState<number | null>(null);
  const vipSpend = spend ?? t.data?.vipSpend ?? 4000;
  const vipOrders = orders ?? t.data?.vipOrders ?? 8;

  return (
    <div className={styles.segEditor}>
      <div className={styles.segEditorH}>Auto-assign thresholds</div>
      <div className={styles.segEditorRow}>
        <label>
          VIP when spend ≥
          <span className={styles.segNum}>
            <span className={styles.segNumPre}>RM</span>
            <input
              type="number"
              min="0"
              step="100"
              value={vipSpend}
              onChange={(e) => setSpend(Math.max(0, Number(e.target.value) || 0))}
            />
          </span>
        </label>
        <label>
          …or orders ≥
          <span className={styles.segNum}>
            <input
              type="number"
              min="1"
              step="1"
              value={vipOrders}
              onChange={(e) => setOrders(Math.max(1, Number(e.target.value) || 1))}
            />
          </span>
        </label>
        <button
          type="button"
          className={styles.segReset}
          onClick={() => {
            setSpend(4000);
            setOrders(8);
          }}
        >
          Reset to defaults
        </button>
        <button
          type="button"
          className={styles.segRuleEditBtn}
          disabled={update.isPending}
          onClick={() => update.mutate({ vipSpend, vipOrders })}
        >
          {update.isPending ? "Saving…" : "Save"}
        </button>
      </div>
      <div className={styles.segEditorNote}>
        New = no orders · Repeat buyer = everyone else with at least one order. Changes re-segment
        everyone live (except manual overrides).
      </div>
    </div>
  );
}

function CreateAudienceModal({
  businessId,
  captured,
  anyFilter,
  matchCount,
  totalCount,
  existing,
  onClose,
  onCreated,
}: {
  businessId: string;
  captured: AudienceCriteria;
  anyFilter: boolean;
  matchCount: number;
  totalCount: number;
  existing: string[];
  onClose: () => void;
  onCreated: (label: string, count: number) => void;
}) {
  const create = useCreateAudience(businessId);
  const upload = useUploadAudience(businessId);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [tab, setTab] = useState<"filter" | "upload">("filter");
  const [name, setName] = useState("");
  const [drag, setDrag] = useState(false);
  const [parsed, setParsed] = useState<{ fileName: string; phones: number; csv: string } | null>(
    null,
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filterChips: [string, string][] = [
    ...(captured.segment ?? []).map((v): [string, string] => ["Segment", SEG_LABEL[v]]),
    ...(captured.platform ?? []).map((v): [string, string] => ["Source", v]),
    ...(captured.optin ? ([["Opt-in", "Yes"]] as [string, string][]) : []),
    ...(captured.q ? ([["Search", `“${captured.q}”`]] as [string, string][]) : []),
  ];

  const dupe = !!name.trim() && existing.some((e) => e.toLowerCase() === name.trim().toLowerCase());
  const canCreate =
    !!name.trim() && !dupe && (tab === "filter" ? true : !!parsed && parsed.phones > 0);
  const previewCount = tab === "filter" ? matchCount : (parsed?.phones ?? 0);

  function readFile(file: File | undefined) {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => {
      const text = String(r.result ?? "");
      const tokens = (text.match(/[\d][\d\s\-()+]{7,}\d/g) ?? []).map((s) =>
        s.replace(/[^\d]/g, ""),
      );
      const phones = [...new Set(tokens.filter((d) => d.length >= 8))];
      setParsed({ fileName: file.name, phones: phones.length, csv: text });
      if (!name) setName(file.name.replace(/\.[^.]+$/, ""));
    };
    r.readAsText(file);
  }

  function downloadTemplate() {
    const csv = "name,phone\nAhmad Faizal,+60 19-332 8841\nSiti Noraini,+60 13-887 2210\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "audience-template.csv";
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  async function submit() {
    const label = name.trim();
    if (!label || dupe) return;
    if (tab === "filter") {
      await create.mutateAsync({ label, kind: "filter", definition: captured });
      onCreated(label, matchCount);
    } else if (parsed) {
      const r = await upload.mutateAsync({ label, csv: parsed.csv });
      onCreated(label, r.matched);
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close
    <div className={styles.modalScrim} onMouseDown={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Create audience"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalH}>
          <span className={styles.modalT}>Create audience</span>
          <button type="button" className={styles.modalX} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={styles.audTabs}>
          <button
            type="button"
            className={`${styles.audTab}${tab === "filter" ? ` ${styles.on}` : ""}`}
            onClick={() => setTab("filter")}
          >
            From current filter
          </button>
          <button
            type="button"
            className={`${styles.audTab}${tab === "upload" ? ` ${styles.on}` : ""}`}
            onClick={() => setTab("upload")}
          >
            Upload a list
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.field}>
            <label htmlFor="aud-name">Audience name</label>
            <input
              id="aud-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Raya VIPs — Terengganu"
            />
            {dupe && (
              <div className={styles.audWarn}>An audience with this name already exists.</div>
            )}
          </div>

          {tab === "filter" && (
            <div className={styles.field}>
              {/* biome-ignore lint/a11y/noLabelWithoutControl: section caption, not a form label */}
              <label>Members — current Contacts filter</label>
              {!anyFilter ? (
                <div className={styles.audAllnote}>
                  No column filters active — this saves{" "}
                  <b>all {totalCount.toLocaleString()} contacts</b>. Tip: filter the table first
                  (segment, source, opt-in…) to target a subset.
                </div>
              ) : (
                <div className={styles.audChips}>
                  {filterChips.map(([k, v]) => (
                    <span className={styles.audCrit} key={`${k}-${v}`}>
                      <span className={styles.audCritK}>{k}</span>
                      {v}
                    </span>
                  ))}
                </div>
              )}
              <div className={styles.audCount}>
                {Ic.check} <b>{matchCount.toLocaleString()}</b> contacts match
                {anyFilter ? " this filter" : ""}
              </div>
            </div>
          )}

          {tab === "upload" && (
            <div className={styles.field}>
              {/* biome-ignore lint/a11y/noLabelWithoutControl: caption above the drop zone */}
              <label>Upload CSV / contact list</label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                hidden
                onChange={(e) => readFile(e.target.files?.[0])}
              />
              <button
                type="button"
                className={`${styles.audDrop}${drag ? ` ${styles.drag}` : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDrag(false);
                  readFile(e.dataTransfer.files[0]);
                }}
                onClick={() => fileRef.current?.click()}
              >
                {!parsed ? (
                  <>
                    <span className={styles.audDropIc}>{Ic.tag}</span>
                    <span className={styles.audDropT}>
                      Drop a CSV here or <span className={styles.lnk}>browse</span>
                    </span>
                    <span className={styles.audDropS}>
                      A column of phone numbers (with optional name). Numbers are matched to
                      existing contacts.
                    </span>
                  </>
                ) : (
                  <>
                    <span className={styles.audDropT}>
                      {Ic.check} <b>{parsed.fileName}</b>
                    </span>
                    <span className={styles.audDropS}>
                      {parsed.phones.toLocaleString()} numbers found
                    </span>
                    <span className={styles.lnk}>Replace file</span>
                  </>
                )}
              </button>
              <button
                type="button"
                className={styles.audTmpl}
                onClick={(e) => {
                  e.stopPropagation();
                  downloadTemplate();
                }}
              >
                ↓ Download CSV template
              </button>
            </div>
          )}
        </div>

        <div className={styles.modalFoot}>
          <button type="button" className={styles.btnGhost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn btn--primary ${styles.headBtn}`}
            disabled={!canCreate || create.isPending || upload.isPending}
            onClick={submit}
          >
            {Ic.plus} Create{previewCount ? ` · ${previewCount.toLocaleString()}` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Bulk CSV import — CREATES contacts (the audience upload only matches existing
 *  ones). New rows arrive opted-in (R-OPT-1 default) with optin_source 'import';
 *  rows that already exist are left untouched apart from filling an empty name. */
function ImportContactsModal({ businessId, onClose }: { businessId: string; onClose: () => void }) {
  const importContacts = useImportContacts(businessId);
  const fileRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [file, setFile] = useState<{
    name: string;
    csv: string;
    valid: number;
    named: number;
  } | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  function readFile(f: File | undefined) {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const csv = String(reader.result ?? "");
      const rows = parseContactCsv(csv);
      const phones = new Set(rows.map((r) => r.phone).filter(Boolean));
      const named = rows.filter((r) => r.phone && r.name).length;
      setError(null);
      setResult(null);
      setFile({ name: f.name, csv, valid: phones.size, named });
    };
    reader.readAsText(f);
  }

  function downloadTemplate() {
    const csv = "name,phone\nAhmad Faizal,+60 19-332 8841\nNurul Aina,0176470121\n";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "contacts-import-template.csv";
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  async function submit() {
    if (!file || file.valid === 0) return;
    setError(null);
    try {
      setResult(await importContacts.mutateAsync({ csv: file.csv }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed.");
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close
    <div className={styles.modalScrim} onMouseDown={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Import contacts"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalH}>
          <span className={styles.modalT}>Import contacts</span>
          <button type="button" className={styles.modalX} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={styles.modalBody}>
          {result ? (
            <div className={styles.field}>
              <div className={styles.audCount}>
                {Ic.check} <b>{result.imported.toLocaleString()}</b> new contact
                {result.imported === 1 ? "" : "s"} imported
              </div>
              <div className={styles.audAllnote}>
                {result.existing > 0 && (
                  <>
                    <b>{result.existing.toLocaleString()}</b> already existed (left untouched).{" "}
                  </>
                )}
                {result.invalid > 0 && (
                  <>
                    <b>{result.invalid.toLocaleString()}</b> row
                    {result.invalid === 1 ? "" : "s"} skipped (not a valid MY number).{" "}
                  </>
                )}
                New contacts are opted-in by default and count toward audiences immediately.
              </div>
            </div>
          ) : (
            <div className={styles.field}>
              {/* biome-ignore lint/a11y/noLabelWithoutControl: caption above the drop zone */}
              <label>CSV / contact list</label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                hidden
                onChange={(e) => readFile(e.target.files?.[0])}
              />
              <button
                type="button"
                className={`${styles.audDrop}${drag ? ` ${styles.drag}` : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDrag(false);
                  readFile(e.dataTransfer.files[0]);
                }}
                onClick={() => fileRef.current?.click()}
              >
                {!file ? (
                  <>
                    <span className={styles.audDropIc}>{Ic.person}</span>
                    <span className={styles.audDropT}>
                      Drop a CSV here or <span className={styles.lnk}>browse</span>
                    </span>
                    <span className={styles.audDropS}>
                      Columns: phone (required) and name (optional). New numbers become contacts;
                      existing ones are never overwritten.
                    </span>
                  </>
                ) : (
                  <>
                    <span className={styles.audDropT}>
                      {Ic.check} <b>{file.name}</b>
                    </span>
                    <span className={styles.audDropS}>
                      {file.valid.toLocaleString()} valid number{file.valid === 1 ? "" : "s"} ·{" "}
                      {file.named.toLocaleString()} with names
                    </span>
                    <span className={styles.lnk}>Replace file</span>
                  </>
                )}
              </button>
              <button
                type="button"
                className={styles.audTmpl}
                onClick={(e) => {
                  e.stopPropagation();
                  downloadTemplate();
                }}
              >
                ↓ Download CSV template
              </button>
              {file && file.valid === 0 && (
                <div className={styles.audWarn}>No valid Malaysian numbers in this file.</div>
              )}
              {error && <div className={styles.audWarn}>{error}</div>}
            </div>
          )}
        </div>

        <div className={styles.modalFoot}>
          <button type="button" className={styles.btnGhost} onClick={onClose}>
            {result ? "Done" : "Cancel"}
          </button>
          {!result && (
            <button
              type="button"
              className={`btn btn--primary ${styles.headBtn}`}
              disabled={!file || file.valid === 0 || importContacts.isPending}
              onClick={submit}
            >
              {importContacts.isPending
                ? "Importing…"
                : `Import${file ? ` · ${file.valid.toLocaleString()}` : ""}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactDrawer({
  c,
  businessId,
  onClose,
  onOpenChat,
}: {
  c: ContactRow;
  businessId: string;
  onClose: () => void;
  onOpenChat: () => void;
}) {
  const rename = useRenameContact(businessId);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const displayName = c.name ?? c.wa_name;
  async function saveName() {
    const v = nameDraft.trim();
    await rename.mutateAsync({ id: c.id, name: v || null });
    setEditingName(false);
  }

  const seg = c.segment;
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: scrim click-to-close
    <div className={styles.drawerScrim} onMouseDown={onClose}>
      <aside
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-label="Contact detail"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.drawerH}>
          <div>
            <div className={styles.drawerEyebrow}>Contact</div>
            <div className={styles.drawerTitle}>{displayName ?? c.phone}</div>
          </div>
          <button type="button" className={styles.drawerX} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className={styles.drawerBody}>
          <div className={styles.cprofile}>
            <div className={styles.cprofileId}>
              {initialsFor(displayName) ? (
                <div
                  className={`${styles.cprofileAv} ${styles.avInitials}`}
                  style={avatarStyle(c.phone)}
                >
                  {initialsFor(displayName)}
                </div>
              ) : (
                <div className={styles.cprofileAv}>{Ic.person}</div>
              )}
              {editingName ? (
                <div className={styles.nameEditRow}>
                  <input
                    className={styles.nameEditInput}
                    value={nameDraft}
                    placeholder={c.wa_name ?? "Custom name"}
                    maxLength={120}
                    // biome-ignore lint/a11y/noAutofocus: the row only exists after an explicit edit click
                    autoFocus
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveName();
                      if (e.key === "Escape") setEditingName(false);
                    }}
                  />
                  <button
                    type="button"
                    className={styles.nameEditSave}
                    disabled={rename.isPending}
                    onClick={saveName}
                  >
                    Save
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.nameEditBtn}
                  title="Set a custom name"
                  onClick={() => {
                    setNameDraft(c.name ?? "");
                    setEditingName(true);
                  }}
                >
                  <span className={styles.cprofileName}>{displayName ?? "—"}</span>
                  <span className={styles.namePencil} aria-hidden="true">
                    ✎
                  </span>
                </button>
              )}
              {c.name && c.wa_name && c.wa_name !== c.name && (
                <div className={styles.cprofileWaName}>~ {c.wa_name} · WhatsApp name</div>
              )}
              <div className={styles.cprofilePhone}>{c.phone}</div>
              <div className={styles.cprofileTags}>
                <span className={`${styles.tagpill} ${seg === "vip" ? styles.info : styles.muted}`}>
                  {SEG_LABEL[seg]}
                </span>
                {c.optin ? (
                  <span className={`${styles.optin} ${styles.optinYes}`}>{Ic.check} Opted-in</span>
                ) : (
                  <span className={`${styles.optin} ${styles.optinNo}`}>Not opted-in</span>
                )}
              </div>
            </div>

            <div className={styles.cprofileStats}>
              <div>
                <span className={styles.cpsK}>Orders</span>
                <span className={`${styles.cpsV} ${styles.mono}`}>{c.orders_count}</span>
              </div>
              <div>
                <span className={styles.cpsK}>Spend</span>
                <span className={`${styles.cpsV} ${styles.mono}`}>
                  {fmtRM(Number(c.total_spend))}
                </span>
              </div>
              <div>
                <span className={styles.cpsK}>Source</span>
                <span className={styles.cpsV}>{c.source_platform ?? "—"}</span>
              </div>
              <div>
                <span className={styles.cpsK}>Region</span>
                <span className={styles.cpsV}>{c.region ?? "—"}</span>
              </div>
            </div>

            <div>
              <div className={styles.cprofileSecH}>{Ic.receipt} Recent orders</div>
              <div className={styles.cprofileEmpty}>
                {c.orders_count === 0
                  ? "No orders yet · new customer."
                  : "Order history coming soon."}
              </div>
            </div>

            <div>
              <div className={styles.cprofileSecH}>{Ic.people} Audiences</div>
              <div className={styles.cprofileEmpty}>Not in any audience.</div>
            </div>

            <button
              type="button"
              className={`${styles.btnSend} ${styles.cprofileChat}`}
              onClick={onOpenChat}
            >
              {Ic.chat} Open chat
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
